import { Check, Quote, Star, Award } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { assetUrl } from "@/lib/api";
import { Eyebrow, Reveal, SectionTitle } from "./Primitives";
import { LeadForm } from "./LeadForm";
import { VideoTestimonial } from "./VideoTestimonial";
import { Cta } from "./Cta";

export const Consultation = () => {
  const { content } = useSite();
  const c = content.consultation || {};
  return (
    <section id="consultation" data-testid="consultation-section" className="bg-white py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <SectionTitle>{c.title}</SectionTitle>
          <p className="t-body mt-6 max-w-lg text-sage">{c.text}</p>
          <ul className="mt-8 space-y-3">
            {(c.points || []).map((pt) => (
              <li key={pt} className="t-strong flex items-center gap-3 text-forest">
                <span className="grid h-7 w-7 place-items-center rounded-full bg-sand"><Check className="h-4 w-4 text-leaf" aria-hidden /></span>{pt}
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <Cta kind="call" name="expert_call" position="consultation" variant="outline" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="rounded-[32px] bg-bone p-6 ring-1 ring-forest/10 sm:p-10">
          <p className="mb-6 text-[22px] font-extrabold tracking-[-0.01em] text-forest sm:text-2xl">{content.cta_text?.consult}</p>
          <LeadForm ctaName="expert_consultation" position="consultation" idPrefix="inline-lead" />
        </Reveal>
      </div>
    </section>
  );
};

export const Testimonials = () => {
  const { content, t: tr } = useSite();
  const list = (content.testimonials || []).filter((t) => (t.text || t.video) && t.name && t.published !== false);
  if (!list.length) return null;
  return (
    <section id="testimonials" data-testid="testimonials-section" className="bg-sand py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mb-12 max-w-2xl">
          <Eyebrow>{tr("testimonials_eyebrow")}</Eyebrow>
          <SectionTitle>{tr("testimonials_title")}</SectionTitle>
        </Reveal>
        <div className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 [scrollbar-width:none] md:mx-0 md:grid md:grid-cols-2 md:gap-5 md:overflow-visible md:px-0 lg:grid-cols-3">
          {list.map((t, i) => (
            <Reveal key={`${t.name}-${i}`} delay={(i % 3) * 0.07} className="w-[78vw] max-w-[340px] shrink-0 snap-center md:w-auto md:max-w-none">
              {t.video ? <VideoTestimonial item={t} index={i} /> : (
              <figure data-testid={`testimonial-card-${i}`} className="flex h-full flex-col rounded-[28px] bg-white p-7 sm:p-8">
                <Quote className="h-8 w-8 text-gold" aria-hidden />
                {Number(t.rating) > 0 && (
                  <div className="mt-4 flex gap-0.5" aria-label={`${t.rating} out of 5`}>
                    {Array.from({ length: Math.min(5, Number(t.rating)) }).map((_, k) => <Star key={k} className="h-4 w-4 fill-gold text-gold" aria-hidden />)}
                  </div>
                )}
                <blockquote className="mt-4 flex-1 text-[16px] leading-relaxed text-ink">{t.text}</blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  {t.image && <img src={assetUrl(t.image)} alt={t.name} loading="lazy" width="48" height="48" className="h-12 w-12 rounded-full object-cover" />}
                  <span><span className="block font-semibold text-forest">{t.name}</span>{t.location && <span className="text-sm text-sage">{t.location}</span>}</span>
                </figcaption>
              </figure>
              )}
            </Reveal>
          ))}
        </div>
        <p data-testid="testimonials-disclaimer" className="mt-8 max-w-3xl text-[15px] font-medium leading-relaxed text-sage">{tr("testimonials_note")}</p>
      </div>
    </section>
  );
};

export const Certificates = () => {
  const { content, t: tr } = useSite();
  const list = (content.certificates || []).filter((c) => c.title && c.image && c.published !== false);
  if (!list.length) return null;
  return (
    <section id="quality" data-testid="certificates-section" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mb-12 max-w-2xl">
          <Eyebrow>{tr("certs_eyebrow")}</Eyebrow>
          <SectionTitle>{tr("certs_title")}</SectionTitle>
        </Reveal>
        <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
          {list.map((c, i) => (
            <Reveal key={`${c.title}-${i}`} delay={(i % 4) * 0.06}>
              <a href={assetUrl(c.image)} target="_blank" rel="noopener noreferrer" data-testid={`certificate-card-${i}`} className="group block rounded-[24px] border border-forest/10 bg-bone p-3 transition-transform duration-500 hover:-translate-y-1">
                <div className="aspect-[3/4] overflow-hidden rounded-2xl bg-white">
                  <img src={assetUrl(c.image)} alt={c.title} loading="lazy" className="h-full w-full object-contain p-2 transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="flex items-start gap-2 p-3">
                  <Award className="mt-0.5 h-4 w-4 shrink-0 text-leaf" aria-hidden />
                  <span><span className="block text-[15px] font-bold text-forest">{c.title}</span>{c.issuer && <span className="block text-sm text-sage">{c.issuer}</span>}{c.number && <span className="block text-sm font-semibold text-ink">No. {c.number}</span>}</span>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
